import React, { useState, useEffect, useRef } from 'react';
import { customersAPI } from '../services/api';
import AddCustomerModal from '../components/AddCustomerModal';
import CustomerDetailModal from '../components/CustomerDetailModal';
import ImportCustomersModal from '../components/ImportCustomersModal';
import { formatDateInIsrael } from '../utils/dateUtils';

function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getAll();
      console.log('Customers loaded:', response.data);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = async (customer) => {
    try {
      // Use identity_id if available, otherwise use id
      const identifier = customer.identity_id || customer.id;
      const response = await customersAPI.getById(identifier);
      console.log('Customer data loaded:', response.data);
      setSelectedCustomer(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading customer details:', error);
    }
  };

  const handleAddNew = () => {
    setShowAddModal(true);
    setShowAddMenu(false);
  };

  const handleImportExcel = async () => {
    setShowImportModal(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const response = await customersAPI.import(file);
      console.log('Import response:', response.data);
      
      // Reload customers list
      await loadCustomers();
      showSuccess('לקוחות יובאו בהצלחה!');
      setShowAddMenu(false);
    } catch (error) {
      console.error('Error importing customers:', error);
      alert('שגיאה ביבוא הלקוחות: ' + error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleExport = async () => {
    try {
      const response = await customersAPI.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error exporting customers:', error);
      alert('שגיאה בייצוא הנתונים');
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.identity_id.includes(searchTerm)
  );

  if (loading) {
    return <div className="loading">טוען לקוחות...</div>;
  }

  return (
    <div className="customers-page">
      <h1 className="page-title">👥 ניהול לקוחות</h1>

      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      <div className="top-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="חפש לפי שם, תעודת זהות או אימייל..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="action-buttons">
          <div className="add-customer-wrapper">
            <button 
              className="btn btn-primary add-customer-btn"
              onClick={() => setShowAddMenu(!showAddMenu)}
              title="הוסף לקוח חדש"
            >
              ➕ הוסף לקוח
            </button>
            
            {showAddMenu && (
              <div className="add-menu-dropdown">
                <button 
                  className="menu-item"
                  onClick={handleAddNew}
                >
                  <span className="menu-icon">📝</span>
                  <span>הוסף לקוח חדש</span>
                </button>
                <button 
                  className="menu-item"
                  onClick={handleImportExcel}
                >
                  <span className="menu-icon">📊</span>
                  <span>יבוא מאקסל</span>
                </button>
              </div>
            )}
          </div>

          <button className="btn btn-warning" onClick={handleExport}>
            📥 ייצוא לאקסל
          </button>
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <div className="empty-state">
          <h3>אין לקוחות</h3>
          <p>התחל בהוספת לקוחות לספריה</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>תעודת זהות</th>
              <th>שם</th>
              <th>אימייל</th>
              <th>טלפון</th>
              <th>תוקף מנוי</th>
              <th>קנסות</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr
                key={customer.identity_id}
                onClick={() => handleSelectCustomer(customer)}
                style={{ cursor: 'pointer' }}
              >
                <td>{customer.identity_id}</td>
                <td>{customer.name}</td>
                <td>{customer.email || '-'}</td>
                <td>{customer.phone || '-'}</td>
                <td>
                  {customer.subscription_start_date && customer.subscription_end_date
                    ? `${formatDateInIsrael(customer.subscription_start_date)} - ${formatDateInIsrael(customer.subscription_end_date)}`
                    : 'ללא מנוי'}
                </td>
                <td className={customer.outstanding_fines > 0 ? 'text-danger' : ''}>
                  ₪{parseFloat(customer.outstanding_fines || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showDetailModal && (
        <CustomerDetailModal
          customerIdentityId={selectedCustomer?.customer?.identity_id}
          onClose={() => setShowDetailModal(false)}
          onUpdate={() => {
            loadCustomers();
            //setShowDetailModal(false);
            showSuccess('לקוח נשמר בהצלחה! ✓');
          }}
        />
      )}

      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onCustomerAdded={() => {
            loadCustomers();
            setShowAddModal(false);
            showSuccess('לקוח חדש נוסף בהצלחה! ✓');
          }}
        />
      )}

      {showImportModal && (
        <ImportCustomersModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            loadCustomers();
            setShowImportModal(false);
          }}
        />
      )}

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}

export default CustomersPage;
